import { create } from 'zustand';
import type { RaspiDevice } from '@/types/raspi';
import { getAllRaspis } from '@/services/api';

interface RaspiStore {
  // Raspi Data (Database-Aligned)
  allRaspis: Record<string, RaspiDevice>;
  selectedRaspiId: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchAllRaspis: () => Promise<void>;
  updateRaspiFromWebSocket: (raspi_id: string, data: Partial<RaspiDevice>) => void;
  updateRaspiFromUser: (raspi_id: string, data: Partial<RaspiDevice>) => Promise<boolean>;
  removeRaspi: (raspi_id: string) => Promise<boolean>;
  selectRaspi: (raspi_id: string) => void;
  clearError: () => void;
  resetStore: () => void;
}

export const useRaspiStore = create<RaspiStore>((set, get) => ({
  // Initialize with default values
  allRaspis: {},
  selectedRaspiId: null,
  isLoading: false,
  error: null,

  fetchAllRaspis: async () => {
    set({ isLoading: true, error: null });
    try {
      const raspisData = await getAllRaspis();
      // Convert string dates to Date objects for all raspis
      const convertedRaspis: Record<string, RaspiDevice> = {};
      Object.entries(raspisData).forEach(([id, raspi]) => {
        convertedRaspis[id] = {
          ...raspi,
          last_seen: raspi.last_seen instanceof Date ? raspi.last_seen : new Date(raspi.last_seen),
          last_update: raspi.last_update instanceof Date ? raspi.last_update : new Date(raspi.last_update),
        };
      });
      // ✅ Fix 2: Merge instead of replace - preserve WebSocket updates
      set((state) => ({
        allRaspis: {
          ...state.allRaspis,  // Keep existing store data (may have WebSocket updates)
          ...convertedRaspis,  // Merge with API data (adds new entities, updates existing)
        },
        isLoading: false,
      }));
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to connect to hub server',
        isLoading: false,
        allRaspis: {} // Clear devices when hub is not available
      });
    }
  },

  updateRaspiFromWebSocket: (raspi_id: string, data: Partial<RaspiDevice>) => {
    // Update store only - no API call since data is already from database
    set((state) => {
      const existingRaspi = state.allRaspis[raspi_id];
      if (!existingRaspi) return state;
      
      const updatedRaspi: RaspiDevice = {
        ...existingRaspi,
        ...data,
        last_update: new Date(),
      };
      
      return {
        allRaspis: {
          ...state.allRaspis,
          [raspi_id]: updatedRaspi,
        },
      };
    });
  },

  updateRaspiFromUser: async (raspi_id: string, _data: Partial<RaspiDevice>) => {
    
    // VALIDATION: Ensure RASPI exists in store before any operation
    const originalData = get().allRaspis[raspi_id];
    if (!originalData) {
      console.error('🔄 [RASPI STORE] ERROR: Cannot update non-existent RASPI:', raspi_id);
      return false;
    }
    
    // VALIDATION: RASPI updates are not supported for now
    console.error('🔄 [RASPI STORE] ERROR: RASPI updates not supported');
    return false;
  },

  removeRaspi: async (raspi_id: string) => {
    
    // Capture original data BEFORE optimistic update
    const originalData = get().allRaspis[raspi_id];
    
    try {
      // Remove from store immediately (store-first approach)
      set((state) => {
        const { [raspi_id]: removed, ...remainingRaspis } = state.allRaspis;
        return { 
          allRaspis: remainingRaspis,
          selectedRaspiId: state.selectedRaspiId === raspi_id ? null : state.selectedRaspiId,
        };
      });

      // RASPI deletion is not supported for now
      console.error('🔄 [RASPI STORE] ERROR: RASPI deletion not supported');
      
      // Revert store update since deletion is not supported
      set((state) => ({
        allRaspis: {
          ...state.allRaspis,
          [raspi_id]: originalData,
        },
      }));
      return false;
    } catch (error) {
      console.error('🔄 [RASPI STORE] Error removing RASPI:', error);
      // Also revert on exception using captured original data
      if (originalData) {
        set((state) => ({
          allRaspis: {
            ...state.allRaspis,
            [raspi_id]: originalData,
          },
        }));
      }
      return false;
    }
  },

  selectRaspi: (raspi_id: string) => {
    set({ selectedRaspiId: raspi_id });
  },

  clearError: () => {
    set({ error: null });
  },

  resetStore: () => {
    set({
      allRaspis: {},
      selectedRaspiId: null,
      isLoading: false,
      error: null,
    });
  },
}));

export default useRaspiStore; 